// navigator.language is often a language tag with no region ("en").
// That tag is not an ISO country, and Stripe rejects the uppercased form ("EN").
// Only an explicit region subtag is a country, and only when the form can select it.

export const regionFromLocale = (locale: string): string | undefined => {
  const normalized = locale.trim().replace(/_/g, '-');
  if (!normalized) {
    return undefined;
  }

  try {
    return new Intl.Locale(normalized).region?.toUpperCase();
  } catch {
    return undefined;
  }
};

export const getDefaultCountryCode = (
  locales: readonly string[],
  allowedCodes: readonly string[],
): string => {
  const allowed = new Set(allowedCodes);
  for (const locale of locales) {
    const region = regionFromLocale(locale);
    if (region && allowed.has(region)) {
      return region;
    }
  }
  return '';
};

// A stored country can sit outside the short dropdown, and Connect Express
// submits it with no picker. Keep that country when Stripe supports it.
// Browser defaults still have to be a country the open picker can show.
export const resolveInitialExpressCountry = (
  storedCountries: readonly (string | undefined)[],
  locales: readonly string[],
  visibleCodes: readonly string[],
  acceptedStoredCodes: readonly string[],
): string => {
  const acceptedStored = new Set(acceptedStoredCodes);
  for (const stored of storedCountries) {
    const code = stored?.trim().toUpperCase() ?? '';
    if (code && acceptedStored.has(code)) {
      return code;
    }
  }
  return getDefaultCountryCode(locales, visibleCodes);
};

export const browserLocales = (): readonly string[] => {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (navigator.languages?.length) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
};
